import React, { useState, useEffect, useRef } from 'react';
import { Bot, RefreshCw, Activity, Send, User, Cpu, Cloud, Leaf, FlaskConical, Sprout, BarChart, Satellite, Camera } from 'lucide-react';
import Card from '../components/ui/Card';
import MarkdownText from '../components/ui/MarkdownText';
import ApiService from '../services/apiService';
import TranslationService from '../services/translationService';
import { commitModule } from '../utils/reportMarkers';

const AIConsultantView = ({ t, isRTL, reportData, lang, setReportData }) => {
    const [history, setHistory] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [customLoadingText, setCustomLoadingText] = useState("");
    const scrollRef = useRef(null);
    const [toasts, setToasts] = useState([]);

    // Options from backend
    const [options, setOptions] = useState({
        devices: [],
        leaf_scans: [],
        soil_scans: [],
        crop_recs: [],
        yield_preds: [],
        satellites: [],
        aerial_palms: []
    });

    // Selections
    const [includeUserInfo, setIncludeUserInfo] = useState(false);
    const [includeMacroWeather, setIncludeMacroWeather] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState("");
    const [selectedLeafScan, setSelectedLeafScan] = useState("");
    const [selectedSoilScan, setSelectedSoilScan] = useState("");
    const [selectedCropRec, setSelectedCropRec] = useState("");
    const [selectedYieldPred, setSelectedYieldPred] = useState("");
    const [selectedSatellite, setSelectedSatellite] = useState("");
    const [selectedAerialPalm, setSelectedAerialPalm] = useState("");

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const res = await ApiService.get('/ai-consultant/options?user_id=1');
                if (res) {
                    setOptions({
                        devices: res.devices || [],
                        leaf_scans: res.leaf_scans || [],
                        soil_scans: res.soil_scans || [],
                        crop_recs: res.crop_recs || [],
                        yield_preds: res.yield_preds || [],
                        satellites: res.satellites || [],
                        aerial_palms: res.aerial_palms || []
                    });
                }
            } catch (e) {
                console.error("Failed to fetch options", e);
            }
        };
        fetchOptions();
    }, []);

    const showToast = (msg) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, msg }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    };

    const currentHistoryLang = useRef(lang);

    // `updatedHistory` is always handed in as a local array by the caller and is never read
    // from the `history` useState value -- reading that inside an async handler would
    // capture the stale render-time value (contract 1.4).
    const updateConsultantReport = (updatedHistory) => {
        const reportHistory = updatedHistory.length > 1 ? updatedHistory.slice(1) : [];
        const text = reportHistory
            .map(m => m.role === 'user' ? `Farmer: ${m.content}` : m.content)
            .join('\n\n');
        // This view is the sole owner of ai_consultant_text (contract 1.5) and overwrites it
        // wholesale. commitModule writes the field and the 'consultant' run marker in ONE
        // state update, so the export modal's gate can tell 'ran' from 'never ran'.
        commitModule(setReportData, 'consultant', { ai_consultant_text: text }, true, null);
    };

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [history]);

    useEffect(() => {
        const switchLang = async () => {
            if (history.length > 1 && currentHistoryLang.current !== lang) {
                setLoading(true);

                const newHistory = await Promise.all(history.map(async (msg) => {
                    if (msg.role === 'assistant') {
                        if (msg.originalLang === lang) {
                            return { ...msg, content: msg.originalContent };
                        } else {
                            const translated = await TranslationService.translateText(msg.originalContent, lang);
                            return { ...msg, content: translated };
                        }
                    }
                    return msg;
                }));

                setHistory(newHistory);
                updateConsultantReport(newHistory);
                currentHistoryLang.current = lang;
                setLoading(false);
            } else {
                currentHistoryLang.current = lang;
            }
        };
        switchLang();
    }, [lang]);

    const buildComprehensivePrompt = async () => {
        const isAr = lang === 'ar';
        if (isAr) {
            return `يرجى تحليل بيانات مزرعتي المحددة وتقديم تقرير مفصل.`;
        } else {
            return `Please analyze my selected farm data and provide a detailed report.`;
        }
    };

    const sendToBackend = async (messagesList) => {
        const payload = {
            history: messagesList.map(msg => ({ role: String(msg.role), content: String(msg.content) })),
            lang: lang || (isRTL ? 'ar' : 'en'),
            include_user_info: includeUserInfo,
            include_macro_weather: includeMacroWeather,
            selected_device_ids: selectedDevice ? [Number(selectedDevice)] : [],
            selected_leaf_scans: selectedLeafScan ? [Number(selectedLeafScan)] : [],
            selected_soil_scans: selectedSoilScan ? [Number(selectedSoilScan)] : [],
            selected_crop_recs: selectedCropRec ? [Number(selectedCropRec)] : [],
            selected_yield_preds: selectedYieldPred ? [Number(selectedYieldPred)] : [],
            selected_satellites: selectedSatellite ? [Number(selectedSatellite)] : [],
            selected_aerial_palms: selectedAerialPalm ? [Number(selectedAerialPalm)] : [],
        };

        try {
            // 1. Start Background Task
            const startRes = await ApiService.post('/ai-consultant/start', payload);
            if (!startRes || !startRes.task_id) {
                throw new Error("Failed to start AI task");
            }
            window.dispatchEvent(new CustomEvent('refresh-credits'));

            const taskId = startRes.task_id;

            // 2. Poll for Status
            let attempts = 0;
            const maxAttempts = 60; // 60 attempts * 3s = 180s (3 minutes)

            while (attempts < maxAttempts) {
                attempts++;
                await new Promise(r => setTimeout(r, 3000)); // Wait 3 seconds

                if (attempts === 5) {
                    setCustomLoadingText(isRTL
                        ? "جاري المعالجة. يرجى الانتظار بينما نقوم بالتحليل في الخلفية..."
                        : "The AI servers are processing your complex data. This may take up to a minute...");
                }

                const statusRes = await ApiService.get(`/ai-consultant/status/${taskId}`);
                if (statusRes) {
                    if (statusRes.status === "completed") {
                        setCustomLoadingText("");
                        return statusRes.answer;
                    } else if (statusRes.status === "failed") {
                        setCustomLoadingText("");
                        throw new Error(statusRes.error || "AI Task Failed");
                    }
                    // if pending or processing, loop continues
                }
            }

            setCustomLoadingText("");
            return "AI_ERROR_TIMEOUT";
        } catch (e) {
            setCustomLoadingText("");
            throw e;
        }
    };

    const fetchWithAutoRetry = async (historyPayload) => {
        try {
            return await sendToBackend(historyPayload);
        } catch (error) {
            showToast(isRTL
                ? "الخوادم عليها ضغط حالياً. يرجى المحاولة مرة أخرى."
                : "Servers are under high load or an error occurred. Please try again.");
            return "AI Connection Error. Please try again.";
        }
    };

    const startAnalysis = async () => {
        setLoading(true);
        setHistory([]);

        const startMsg = await buildComprehensivePrompt();
        const userMsg = { role: "user", originalContent: startMsg, content: startMsg, originalLang: lang };
        const tempHistory = [userMsg];

        const answer = await fetchWithAutoRetry(tempHistory);

        if (answer === "AI_ERROR_TIMEOUT" || answer === "AI Connection Error. Please try again." || answer.includes("high traffic")) {
            showToast(isRTL ? "فشل الاتصال بالخادم بعد المحاولات. يرجى المحاولة لاحقاً." : "Connection failed after multiple retries. Please try again later.");
            setHistory([]);
            setLoading(false);
            return;
        }

        const assistantMsg = { role: "assistant", originalContent: answer, content: answer, originalLang: lang };
        const finalHistory = [userMsg, assistantMsg];

        setHistory(finalHistory);
        updateConsultantReport(finalHistory);
        setLoading(false);
    };

    const handleRetry = () => {
        if (loading) return;
        startAnalysis();
    };

    const sendMessage = async () => {
        if (!input.trim()) return;
        setLoading(true);

        const userMsg = { role: "user", originalContent: input, content: input, originalLang: lang };
        const newHistory = [...history, userMsg];

        setHistory(newHistory);
        setInput("");

        const answer = await fetchWithAutoRetry(newHistory);

        if (answer === "AI_ERROR_TIMEOUT") {
            showToast(isRTL ? "فشل الاتصال بالخادم بعد دقيقة من المحاولات. يرجى المحاولة لاحقاً." : "Connection failed after 1 minute of retries. Please try again later.");
            setHistory(history);
            setInput(input);
            setLoading(false);
            return;
        }

        const assistantMsg = { role: "assistant", originalContent: answer, content: answer, originalLang: lang };
        const finalHistory = [...newHistory, assistantMsg];

        setHistory(finalHistory);
        updateConsultantReport(finalHistory);
        setLoading(false);
    };

    return (
        <div className="h-[88vh] flex flex-col font-sans max-w-5xl mx-auto w-full bg-white dark:bg-slate-900 rounded-3xl border border-[#1B263B]/10 dark:border-slate-800 shadow-[0_10px_40px_-15px_rgba(27,38,59,0.1)] dark:shadow-none relative overflow-hidden transition-colors duration-300">            {/* Full Screen Loading Overlay */}
            {loading && history.length === 0 && (
                <div className="absolute inset-0 z-50 bg-[#F4EFEA]/95 dark:bg-slate-900/95 backdrop-blur-md flex flex-col items-center justify-center animate-fade-in px-6 text-center">
                    <div className="relative mb-4">
                        <div className="absolute inset-[-10px] border-4 border-[#C47A2C]/20 rounded-full animate-[ping_2.5s_linear_infinite]"></div>
                        <div className="bg-white dark:bg-slate-800 p-6 rounded-full border border-[#1B263B]/10 dark:border-slate-700 relative z-10 shadow-xl">
                            <Bot size={64} className="text-[#C47A2C] animate-bounce" />
                        </div>
                    </div>
                    {customLoadingText ? (
                        <>
                            <h3 className="text-2xl font-black text-[#1B263B] dark:text-white mb-4 animate-pulse">High Traffic Alert</h3>
                            <p className="text-[#C47A2C] font-bold text-lg max-w-md leading-relaxed">{customLoadingText}</p>
                        </>
                    ) : (
                        <>
                            <h3 className="text-3xl font-black text-[#1B263B] dark:text-white mb-2">{t.consultant.loadingTitle || "Synthesizing Farm Data"}</h3>
                            <p className="text-[#6B8F71] font-bold tracking-widest uppercase text-sm animate-pulse">{t.consultant.analyzing || "Analyzing Telemetry..."}</p>
                            <p className="text-gray-500 dark:text-gray-400 font-medium text-sm mt-3">{t.consultant.connecting || "Connecting..."}</p>
                        </>
                    )}
                </div>
            )}

            {/* Header */}
            <div className="p-4 border-b border-[#1B263B]/5 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-3xl flex justify-between items-center z-10">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-[#C47A2C]/10 rounded-xl">
                        <Bot size={28} className="text-[#C47A2C]" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-[#1B263B] dark:text-white tracking-tight">{t.consultant.title || "AI Command Center"}</h3>
                        <p className="text-sm text-[#6B8F71] font-bold flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#6B8F71] shadow-[0_0_8px_rgba(107,143,113,0.8)] animate-pulse"></span>
                            {t.consultant.subtitle || "Always active, always learning."}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <button
                        onClick={handleRetry}
                        disabled={loading}
                        title={isRTL ? "إعادة تحليل البيانات" : "Retry Analysis"}
                        className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-2.5 rounded-xl text-gray-400 dark:text-gray-300 hover:text-[#C47A2C] dark:hover:text-[#C47A2C] transition-all active:scale-95 disabled:opacity-50"
                    >
                        <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
                    </button>
                    {loading && (
                        <div className="flex items-center gap-3 bg-[#6B8F71]/10 px-4 py-2 rounded-full border border-[#6B8F71]/20">
                            <Activity size={16} className="text-[#6B8F71] animate-spin" />
                            <span className="text-xs font-bold text-[#6B8F71] tracking-widest uppercase">{t.consultant.processing || "Working"}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Expansive Chat Canvas */}
            <div className="flex-1 overflow-y-auto px-6 py-8 md:px-12 bg-[#F9FAFB] dark:bg-slate-900 pb-28 custom-scrollbar" ref={scrollRef}>
                {history.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center min-h-full py-3 animate-fade-in max-w-5xl mx-auto px-2">

                        {/* 🟢 High-Tech Horizontal Banner (Comfortable Size) 🟢 */}
                        <div className="w-full relative overflow-hidden bg-gradient-to-r from-[#1B263B] to-[#2a3b5c] dark:from-slate-800 dark:to-slate-900 rounded-3xl p-4 md:p-5 mb-4 shadow-xl border border-[#C47A2C]/20 flex items-center justify-between">
                            <div className="absolute -left-10 -top-10 w-40 h-40 bg-[#C47A2C]/20 rounded-full blur-3xl"></div>
                            <div className="absolute right-0 bottom-0 w-48 h-48 bg-[#6B8F71]/10 rounded-full blur-3xl"></div>

                            <div className="flex items-center gap-4 md:gap-5 relative z-10">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-[#C47A2C] rounded-full animate-ping opacity-20 duration-1000"></div>
                                    <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center shadow-inner transform rotate-3 hover:rotate-0 transition-transform">
                                        <Bot size={28} className="text-[#C47A2C] drop-shadow-md" />
                                    </div>
                                </div>
                                <div className="text-left">
                                    <h2 className="text-lg md:text-2xl font-black text-white tracking-wide flex flex-wrap items-center gap-3">
                                        {t.consultant.commandCenter || "GeoNutria AI Engine"}
                                        <span className="bg-[#6B8F71]/20 text-[#6B8F71] border border-[#6B8F71]/30 text-[9px] md:text-[10px] px-2.5 py-0.5 rounded-full uppercase tracking-widest font-bold flex items-center gap-1.5 shadow-sm">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#6B8F71] animate-pulse"></span> Online
                                        </span>
                                    </h2>
                                    <p className="text-gray-300 text-xs md:text-sm font-medium mt-1 opacity-90">
                                        {t.consultant.commandDesc || "Configure your data streams below for a comprehensive agricultural synthesis."}
                                    </p>
                                </div>
                            </div>
                            <div className="hidden md:block relative z-10 opacity-20">
                                <Activity size={48} className="text-white" />
                            </div>
                        </div>

                        {/* 🟢 Bento Box Layout (Perfect Spacing) 🟢 */}
                        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 text-left mt-1 relative z-10">

                            <div className="absolute top-10 right-10 w-48 h-48 bg-[#C47A2C]/10 rounded-full -z-10 blur-3xl"></div>
                            <div className="absolute bottom-10 left-10 w-48 h-48 bg-[#6B8F71]/10 rounded-full -z-10 blur-3xl"></div>

                            {/* Box 1: Core Context (Toggles) */}
                            <div className="md:col-span-1 bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl border border-white/60 dark:border-slate-700/50 rounded-3xl p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col justify-center gap-5 hover:bg-white/70 dark:hover:bg-slate-800/70 transition-colors">
                                <div className="flex items-center justify-between cursor-pointer group" onClick={() => setIncludeUserInfo(!includeUserInfo)}>
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white/80 dark:bg-slate-700/80 rounded-xl shadow-sm group-hover:scale-110 transition-transform"><User size={16} className="text-[#C47A2C]" /></div>
                                        <span className="text-sm font-bold text-[#1B263B] dark:text-gray-200">{t.consultant.includeUserInfo || "User Profile"}</span>
                                    </div>
                                    <div className={`w-10 h-5 flex items-center rounded-full p-1 transition-colors ${includeUserInfo ? 'bg-[#6B8F71]' : 'bg-gray-300 dark:bg-slate-600'}`}>
                                        <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${includeUserInfo ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between cursor-pointer group" onClick={() => setIncludeMacroWeather(!includeMacroWeather)}>
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white/80 dark:bg-slate-700/80 rounded-xl shadow-sm group-hover:scale-110 transition-transform"><Cloud size={16} className="text-[#6B8F71]" /></div>
                                        <span className="text-sm font-bold text-[#1B263B] dark:text-gray-200">{t.consultant.macroWeatherData || "Macro Weather"}</span>
                                    </div>
                                    <div className={`w-10 h-5 flex items-center rounded-full p-1 transition-colors ${includeMacroWeather ? 'bg-[#6B8F71]' : 'bg-gray-300 dark:bg-slate-600'}`}>
                                        <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${includeMacroWeather ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                    </div>
                                </div>
                            </div>

                            {/* Box 2: Field Data (Sensors & Soil) */}
                            <div className="md:col-span-2 bg-gradient-to-br from-white/50 to-white/20 dark:from-slate-800/50 dark:to-slate-800/20 backdrop-blur-xl border border-white/60 dark:border-slate-700/50 rounded-3xl p-4 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col gap-3">
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Field Diagnostics</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                            <Cpu size={14} className="text-[#C47A2C]" /> {t.consultant.deviceData || "IoT Sensors"}
                                        </label>
                                        <select value={selectedDevice} onChange={e => setSelectedDevice(e.target.value)} className="w-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border border-gray-200/50 dark:border-slate-700/50 rounded-xl p-2.5 text-sm text-[#1B263B] dark:text-white outline-none focus:border-[#C47A2C] transition-all cursor-pointer">
                                            <option value="">{t.consultant.none || "None"}</option>
                                            {options.devices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                            <FlaskConical size={14} className="text-amber-500" /> {t.consultant.soilAnalysis || "Soil Scans"}
                                        </label>
                                        <select value={selectedSoilScan} onChange={e => setSelectedSoilScan(e.target.value)} className="w-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border border-gray-200/50 dark:border-slate-700/50 rounded-xl p-2.5 text-sm text-[#1B263B] dark:text-white outline-none focus:border-[#C47A2C] transition-all cursor-pointer">
                                            <option value="">{t.consultant.none || "None"}</option>
                                            {options.soil_scans.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Box 3: Plant Health & Predictions */}
                            <div className="md:col-span-2 bg-gradient-to-bl from-white/50 to-white/20 dark:from-slate-800/50 dark:to-slate-800/20 backdrop-blur-xl border border-white/60 dark:border-slate-700/50 rounded-3xl p-4 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col gap-3">
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Crops & Predictions</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                            <Leaf size={14} className="text-[#6B8F71]" /> {t.consultant.leafDoctor || "Leaf Doctor"}
                                        </label>
                                        <select value={selectedLeafScan} onChange={e => setSelectedLeafScan(e.target.value)} className="w-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border border-gray-200/50 dark:border-slate-700/50 rounded-xl p-2.5 text-sm text-[#1B263B] dark:text-white outline-none focus:border-[#C47A2C] transition-all cursor-pointer">
                                            <option value="">{t.consultant.none || "None"}</option>
                                            {options.leaf_scans.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                            <Sprout size={14} className="text-emerald-500" /> {t.consultant.cropRec || "Crop Rec"}
                                        </label>
                                        <select value={selectedCropRec} onChange={e => setSelectedCropRec(e.target.value)} className="w-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border border-gray-200/50 dark:border-slate-700/50 rounded-xl p-2.5 text-sm text-[#1B263B] dark:text-white outline-none focus:border-[#C47A2C] transition-all cursor-pointer">
                                            <option value="">{t.consultant.none || "None"}</option>
                                            {options.crop_recs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                            <BarChart size={14} className="text-blue-500" /> {t.consultant.cropYield || "Yield Pred"}
                                        </label>
                                        <select value={selectedYieldPred} onChange={e => setSelectedYieldPred(e.target.value)} className="w-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border border-gray-200/50 dark:border-slate-700/50 rounded-xl p-2.5 text-sm text-[#1B263B] dark:text-white outline-none focus:border-[#C47A2C] transition-all cursor-pointer">
                                            <option value="">{t.consultant.none || "None"}</option>
                                            {options.yield_preds.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Box 4: Space & Aerial */}
                            <div className="md:col-span-1 bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl border border-white/60 dark:border-slate-700/50 rounded-3xl p-4 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col gap-3">
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Macro Level</h4>
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                        <Satellite size={14} className="text-indigo-500" /> {t.consultant.satelliteAnalysis || "Satellite"}
                                    </label>
                                    <select value={selectedSatellite} onChange={e => setSelectedSatellite(e.target.value)} className="w-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border border-gray-200/50 dark:border-slate-700/50 rounded-xl p-2.5 text-sm text-[#1B263B] dark:text-white outline-none focus:border-[#C47A2C] transition-all cursor-pointer">
                                        <option value="">{t.consultant.none || "None"}</option>
                                        {options.satellites.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                        <Camera size={14} className="text-teal-500" /> {t.consultant.aerialPalms || "Aerial"}
                                    </label>
                                    <select value={selectedAerialPalm} onChange={e => setSelectedAerialPalm(e.target.value)} className="w-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border border-gray-200/50 dark:border-slate-700/50 rounded-xl p-2.5 text-sm text-[#1B263B] dark:text-white outline-none focus:border-[#C47A2C] transition-all cursor-pointer">
                                        <option value="">{t.consultant.none || "None"}</option>
                                        {options.aerial_palms.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Button kept relatively compact so it doesn't cause scrolling */}
                        <button
                            onClick={startAnalysis}
                            disabled={loading}
                            className="bg-[#C47A2C] hover:bg-[#A36322] text-white px-10 py-3 rounded-full font-bold shadow-[0_8px_20px_rgba(196,122,44,0.3)] transition-all transform hover:-translate-y-1 active:scale-95 flex items-center gap-2 mt-5 text-sm"
                        >
                            <Activity size={18} className={loading ? "animate-spin" : "animate-pulse"} />
                            {t.consultant.startBtn || "Analyze Life & Sensors"}
                        </button>
                    </div>
                )}

                {history.map((msg, i) => (
                    <div key={i} className={`flex w-full mb-10 ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                        {msg.role === 'user' ? (
                            <div className="max-w-[80%] md:max-w-[70%] bg-[#6B8F71] text-white p-4 rounded-3xl rounded-tr-sm shadow-md">
                                <p className="leading-relaxed whitespace-pre-wrap font-medium">
                                    <MarkdownText
                                        text={i === 0
                                            ? (isRTL ? "تم سحب الـبيانات وتجهيزها للتحليل.." : "Data parameters selected. Requesting comprehensive synthesis...")
                                            : msg.content}
                                    />
                                </p>
                            </div>
                        ) : msg.content === "The server is currently experiencing high traffic. Please try again later." ? (
                            <div className="w-full bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 p-4 rounded-xl text-center font-medium shadow-sm mx-auto my-4 max-w-2xl">
                                {msg.content}
                            </div>
                        ) : (
                            <div className="w-full relative pl-4 md:pl-10 before:content-[''] before:absolute before:left-0 before:-top-2 before:bottom-0 before:w-1 before:bg-gradient-to-b before:from-[#C47A2C] before:to-transparent before:rounded-full">
                                <div className="absolute top-0 -left-3 bg-[#1B263B] dark:bg-slate-800 p-1 rounded-full shadow-md hidden md:block">
                                    <Bot size={18} className="text-[#C47A2C]" />
                                </div>
                                <div className="text-[#1B263B] dark:text-gray-200 prose prose-lg md:prose-xl max-w-none prose-headings:font-black prose-headings:text-[#1B263B] dark:prose-headings:text-white prose-a:text-[#C47A2C] prose-strong:text-[#1B263B] dark:prose-strong:text-white prose-ul:list-disc whitespace-pre-wrap">
                                    <MarkdownText text={msg.content} />
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* Typing Animation Bubble */}
                {loading && history.length > 0 && (
                    <div className="flex w-full mb-10 justify-start animate-fade-in">
                        <div className="w-full relative pl-4 md:pl-10 before:content-[''] before:absolute before:left-0 before:-top-2 before:bottom-0 before:w-1 before:bg-gradient-to-b before:from-[#C47A2C] before:to-transparent before:rounded-full">
                            <div className="absolute top-0 -left-3 bg-[#1B263B] dark:bg-slate-800 p-1 rounded-full shadow-md hidden md:block">
                                <Bot size={18} className="text-[#C47A2C]" />
                            </div>
                            <div className="flex gap-1.5 items-center py-3 px-1">
                                <span className="w-2.5 h-2.5 bg-[#C47A2C]/50 dark:bg-[#C47A2C]/60 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                <span className="w-2.5 h-2.5 bg-[#C47A2C]/80 dark:bg-[#C47A2C]/80 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                <span className="w-2.5 h-2.5 bg-[#C47A2C] dark:bg-[#C47A2C] rounded-full animate-bounce"></span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Command Bar (Sticky Footer Wrapper) */}
            {history.length > 0 && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#F9FAFB] dark:from-slate-900 to-transparent pt-10 pb-6 px-4 md:px-0 z-40 flex justify-center pointer-events-none">
                    <div className="w-full max-w-4xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl border border-[#1B263B]/10 dark:border-slate-700 shadow-[0_8px_30px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.5)] flex p-2 rounded-full items-center pointer-events-auto transition-all">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                            placeholder={t.consultant.placeholder || "Ask for further insight..."}
                            className="flex-1 bg-transparent px-6 py-4 text-[#1B263B] dark:text-white font-medium outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
                        />
                        <button
                            onClick={sendMessage}
                            disabled={loading || !input.trim()}
                            className="p-4 bg-[#C47A2C] rounded-full text-white hover:bg-[#A36322] disabled:opacity-30 disabled:bg-gray-200 dark:disabled:bg-slate-700 disabled:text-gray-400 transition-all shadow-md transform hover:scale-105 active:scale-95"
                        >
                            <Send size={22} className={isRTL ? "rotate-180 -ml-1 mt-0.5" : "ml-1 mt-0.5"} />
                        </button>
                    </div>
                </div>
            )}

            {/* Toasts */}
            <div className="absolute top-20 left-1/2 transform -translate-x-1/2 flex flex-col gap-2 z-[1000] pointer-events-none">
                {toasts.map(t => (
                    <div key={t.id} className="bg-[#1B263B] text-white font-bold px-6 py-3 rounded-full shadow-2xl animate-fade-in flex items-center gap-3">
                        <Activity size={20} className="text-[#C47A2C] animate-spin" />
                        {t.msg}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AIConsultantView;
